/**
 * Yearly simulation.
 *
 * `advanceYears` is the only place where time passes. Everything that makes a
 * life feel causal lives here: habits become health, workload becomes burnout,
 * neglect becomes distance, and small savings become an entirely different
 * fifth decade.
 *
 * The ordering matters and is deliberate:
 *   age → education → career → business → market → finance → health →
 *   energy/stress → burnout → relationships → inertia → aspirations →
 *   scheduled effects → crisis checks → mortality
 * Money is settled before wellbeing so that this year's financial pressure is
 * what shows up in this year's stress, not next year's.
 */

import { Rng } from './rng';
import type {
  BurnoutStage,
  Effect,
  PlayerState,
} from './types';
import {
  annualExpenses,
  children,
  clamp,
  clamp100,
  debtPressure,
  financialSecurity,
  lifeLoad,
  lifestyleComfort,
  netWorth,
  queryNumber,
  disposableIncome,
  lifestyleTargetForIncome,
  runwayMonths,
  socialHealth,
  stageForAge,
  supportNetwork,
  totalDebt,
  unsecuredDebt,
  workCapacity,
  activeRegretWeight,
} from './derived';
import {
  ASSET_CLASSES,
  BUSINESS_ARCHETYPES,
  CAREER_FIELDS,
  EDUCATION_PROGRAMS,
  MARKET_REGIMES,
  ASPIRATIONS,
} from './content';
import type { EffectContext } from './effects';
import { applyEffects, pushLog, salaryForRole } from './effects';
import { evaluateAll } from './conditions';

/* ------------------------------------------------------------------ *
 * Market
 * ------------------------------------------------------------------ */

const REGIME_ORDER = ['boom', 'normal', 'soft', 'bust'] as const;

function updateMarket(state: PlayerState, rng: Rng): void {
  const regime = state.market.regime;
  const cfg = MARKET_REGIMES[regime];
  state.market.yearsInRegime += 1;
  // Hazard rises with time spent in the regime; mean duration ≈ meanYears.
  const switchChance = clamp(state.market.yearsInRegime / (cfg.meanYears * 2), 0.08, 0.7);
  if (!rng.chance(switchChance)) return;

  // Transitions are ordered: booms cool into normal, busts recover to soft.
  const transitions: Record<typeof REGIME_ORDER[number], readonly [number, number, number, number]> = {
    boom: [10, 55, 25, 10],
    normal: [22, 34, 30, 14],
    soft: [10, 45, 25, 20],
    bust: [6, 34, 56, 4],
  };
  const idx = rng.weightedIndex([...transitions[regime]]);
  const next = REGIME_ORDER[idx < 0 ? 1 : idx] ?? 'normal';
  if (next !== regime) {
    state.market.regime = next;
    state.market.yearsInRegime = 0;
    if (next === 'bust') {
      pushLog(state, {
        category: 'investment',
        tone: 'negative',
        title: 'Markets fell sharply',
        weight: 4,
        tags: ['market'],
      });
      // A downturn is not just an asset-price event: it puts jobs at risk,
      // and it should reach the player through their own employer.
      const exposed =
        (state.career.status === 'employed' || state.career.status === 'selfEmployed') &&
        state.career.jobSecurity < 78;
      if (exposed && rng.chance(0.55) && !state.pendingEvents.includes('career.layoffRound')) {
        state.pendingEvents.push('career.layoffRound');
      }
    } else if (next === 'boom') {
      pushLog(state, { category: 'investment', tone: 'positive', title: 'Markets are running hot', weight: 3, tags: ['market'] });
    }
  }
}

function applyInvestmentReturns(state: PlayerState, rng: Rng): number {
  const cfg = MARKET_REGIMES[state.market.regime];
  let totalChange = 0;
  for (const holding of Object.values(state.finance.holdings)) {
    if (holding.value <= 0) continue;
    const asset = ASSET_CLASSES[holding.assetId];
    if (!asset) continue;
    const stream = rng.keyed(`market:${state.age}:${holding.assetId}`);
    const drift = asset.expectedReturn + cfg.drift * asset.beta;
    let r = drift + stream.normal(0, asset.volatility * cfg.volMultiplier);
    if (asset.shock && stream.chance(asset.shock.chance)) {
      r += stream.range(asset.shock.min, asset.shock.max);
    }
    r = clamp(r, -0.92, 2.2);
    const change = holding.value * r;
    holding.value = Math.max(0, holding.value + change);
    totalChange += change;
  }
  // Property tracks its own class and is not liquid.
  if (state.finance.property.owned) {
    const asset = ASSET_CLASSES['property'];
    const stream = rng.keyed(`market:${state.age}:propertyValue`);
    const r = clamp(
      (asset?.expectedReturn ?? 0.04) + cfg.drift * (asset?.beta ?? 0.6) + stream.normal(0, (asset?.volatility ?? 0.09) * cfg.volMultiplier),
      -0.4,
      0.5,
    );
    state.finance.property.value = Math.max(0, Math.round(state.finance.property.value * (1 + r)));
  }
  // Retirement assets behave like a balanced portfolio.
  if (state.finance.retirementAssets > 0) {
    const stream = rng.keyed(`market:${state.age}:retirement`);
    const balanced = ASSET_CLASSES['balanced'];
    const r = clamp(
      (balanced?.expectedReturn ?? 0.058) + cfg.drift * 0.7 + stream.normal(0, (balanced?.volatility ?? 0.108) * cfg.volMultiplier),
      -0.5,
      0.6,
    );
    state.finance.retirementAssets = Math.round(state.finance.retirementAssets * (1 + r));
  }
  return Math.round(totalChange);
}

/* ------------------------------------------------------------------ *
 * Education
 * ------------------------------------------------------------------ */

function tickEducation(state: PlayerState): void {
  const enrollment = state.education.enrollment;
  if (!enrollment) return;
  const program = EDUCATION_PROGRAMS[enrollment.programId];
  if (!program) {
    state.education.enrollment = null;
    return;
  }
  state.education.yearsStudied += 1;
  state.education.totalCost += enrollment.annualCost;
  for (const [skill, amount] of Object.entries(program.skillGrowth)) {
    const key = skill as keyof typeof state.skills;
    // Discipline and curiosity modulate how much study actually sticks.
    const absorb = 0.7 + (state.traits.discipline + state.traits.curiosity) / 400;
    state.skills[key] = clamp100(state.skills[key] + (amount ?? 0) * absorb);
  }
  state.habits.learning = clamp100(state.habits.learning + 2);
  enrollment.yearsRemaining -= 1;

  if (enrollment.yearsRemaining <= 0) {
    state.education.enrollment = null;
    applyEffects(
      { state, rng: Rng.fromState(state.rng) },
      [{ k: 'education', op: 'grantCredential', id: program.credential, level: program.level }],
    );
    pushLog(state, {
      category: 'education',
      tone: 'milestone',
      title: `Completed ${program.label}`,
      weight: 8,
      tags: ['education', 'credential'],
    });
    if (state.career.status === 'student') state.career.status = 'unemployed';
  }
}

/* ------------------------------------------------------------------ *
 * Career
 * ------------------------------------------------------------------ */

function tickCareer(state: PlayerState, rng: Rng): void {
  const c = state.career;
  const capacity = workCapacity(state);

  if (c.status === 'employed' || c.status === 'selfEmployed' || c.status === 'founder') {
    c.tenure += 1;
    c.yearsExperience += 1;
    c.yearsSinceAdvance += 1;

    const field = c.fieldId ? CAREER_FIELDS[c.fieldId] : undefined;
    if (field) {
      for (const [skill, amount] of Object.entries(field.skillGrowth)) {
        const key = skill as keyof typeof state.skills;
        // Learning slows as you approach the ceiling, and depends on engagement.
        const headroom = 1 - state.skills[key] / 110;
        const engagement = 0.55 + (c.meaningAlignment / 100) * 0.35 + (state.habits.learning / 100) * 0.25;
        state.skills[key] = clamp100(state.skills[key] + (amount ?? 0) * headroom * engagement * capacity);
      }
      // Reputation accrues from sustained performance, and decays without it.
      const repTarget = clamp100(c.performance * 0.6 + c.roleLevel * 8 + Math.min(20, c.yearsExperience));
      c.reputation = clamp100(c.reputation + (repTarget - c.reputation) * 0.18);
    }

    // Performance follows capacity and workload fit.
    const overload = Math.max(0, c.workload - 78) / 22;
    const perfTarget = clamp100(
      40 + capacity * 40 + (state.traits.conscientiousness - 50) * 0.25 - overload * 22 - (state.burnout.load / 100) * 22,
    );
    c.performance = clamp100(c.performance + (perfTarget - c.performance) * 0.3);

    // Job security drifts toward field stability, adjusted by performance.
    if (field) {
      const secTarget = clamp100(field.stability * 0.6 + c.performance * 0.35 + c.reputation * 0.1);
      c.jobSecurity = clamp100(c.jobSecurity + (secTarget - c.jobSecurity) * 0.25);
    }

    // Salary drift: inflation-ish plus merit, then reconciled with the ladder.
    if (c.status === 'employed' && field) {
      const ladder = salaryForRole(state, field.id, c.roleLevel);
      const merit = 1 + (c.performance - 50) / 900;
      state.finance.salary = Math.round(
        clamp(state.finance.salary * (1.008 * merit), ladder * 0.9, ladder * 1.3),
      );
      // Long tenure without advancement stagnates pay and meaning.
      if (c.yearsSinceAdvance > 5) {
        c.meaningAlignment = clamp100(c.meaningAlignment - 1.5);
      }
      // Automatic promotion when clearly ready — the player still gets offered
      // the choice through events, this is the passive path.
      const nextRole = field.roles[c.roleLevel + 1];
      if (nextRole) {
        const meetsSkills = Object.entries(nextRole.requires).every(
          ([s, v]) => state.skills[s as keyof typeof state.skills] >= (v ?? 0),
        );
        const meetsExp = c.yearsExperience >= (nextRole.minExperience ?? 0);
        // Only the first two rungs are automatic. Senior advancement has to be
        // chosen — it is offered as an event with a real cost attached.
        const autoEligible = c.roleLevel < 2;
        if (autoEligible && meetsSkills && meetsExp && c.performance >= 62 && c.yearsSinceAdvance >= 4) {
          if (rng.chance(0.22 + (c.performance - 62) / 240)) {
            c.roleLevel += 1;
            c.promotions += 1;
            c.yearsSinceAdvance = 0;
            c.tenure = 0;
            c.workload = clamp100((c.workload + nextRole.workload) / 2 + 3);
            state.finance.salary = salaryForRole(state, field.id, c.roleLevel);
            pushLog(state, {
              category: 'career',
              tone: 'positive',
              title: `Promoted to ${nextRole.title}`,
              weight: 6,
              tags: ['career', 'promotion'],
            });
          }
        }
      }
    }
  } else if (c.status === 'unemployed' || c.status === 'break') {
    c.tenure = 0;
    // Skills and professional standing fade out of the market.
    c.reputation = clamp100(c.reputation - 3);
    c.performance = clamp100(c.performance - 2);
  }

  if (c.status === 'retired') {
    // A pension-like income proportional to career attainment and assets.
    if (state.finance.otherIncome <= 0) {
      const base = 9000 + c.roleLevel * 2600 + Math.min(14000, c.yearsExperience * 340);
      state.finance.otherIncome = Math.round(base);
    }
  }
}

/* ------------------------------------------------------------------ *
 * Business
 * ------------------------------------------------------------------ */

function tickBusiness(state: PlayerState, rng: Rng): void {
  const b = state.business;
  if (!b.active || !b.archetypeId) return;
  const arch = BUSINESS_ARCHETYPES[b.archetypeId];
  if (!arch) return;

  b.yearsRunning += 1;
  const stream = rng.keyed(`business:${state.age}`);
  const cfg = MARKET_REGIMES[state.market.regime];

  // Skill and attention drive growth; capacity is the binding constraint.
  let skillBonus = 0;
  for (const s of arch.keySkills) skillBonus += (state.skills[s] - 40) / 100;
  skillBonus = clamp(skillBonus / Math.max(1, arch.keySkills.length), -0.35, 0.4);
  const attention = clamp(workCapacity(state), 0.2, 1.2);

  const growth = clamp(
    arch.growthMean * (1 + skillBonus) * attention +
      cfg.drift * 0.8 +
      stream.normal(0, arch.growthVolatility),
    -0.7,
    1.6,
  );
  b.growth = growth;
  b.revenue = Math.max(0, Math.round(b.revenue * (1 + growth)));
  b.operatingCost = Math.round(b.revenue * arch.costRatio * (1 + Math.max(0, -skillBonus) * 0.3));
  const profit = b.revenue - b.operatingCost;

  const healthTarget = clamp100(
    50 + (profit / Math.max(8000, b.revenue)) * 55 - b.risk * 0.2 + skillBonus * 40,
  );
  b.health = clamp100(b.health + (healthTarget - b.health) * 0.4);
  b.valuation = Math.max(0, Math.round(Math.max(0, profit) * arch.exitMultiple + b.capital * 0.5));
  b.timeDemand = clamp100(arch.timeDemand + (b.revenue > 200000 ? 8 : 0) - (b.health > 70 ? 6 : 0));

  // Failure is state-dependent, not a coin flip.
  const failChance = clamp(
    (arch.risk / 100) * 0.09 * (1 + (60 - b.health) / 60) * (state.market.regime === 'bust' ? 2.2 : 1),
    0,
    0.42,
  );
  if (b.health < 30 && stream.chance(failChance)) {
    applyEffects({ state, rng }, [{ k: 'business', op: 'fail' }]);
    pushLog(state, {
      category: 'business',
      tone: 'negative',
      title: `${arch.label} failed`,
      detail: `After ${b.yearsRunning} years.`,
      weight: 9,
      tags: ['business', 'failure'],
    });
    state.counters['crises'] = (state.counters['crises'] ?? 0) + 1;
    state.pendingEvents.push('crisis.rebuild');
  }
}

/* ------------------------------------------------------------------ *
 * Finance
 * ------------------------------------------------------------------ */

/**
 * Settle the year's money.
 *
 * The important rule here is that a household cannot spend money it does not
 * have indefinitely. Faced with a shortfall the player involuntarily downshifts
 * their lifestyle before borrowing, and borrowing itself is capped. Without
 * those two limits the model produces exponential debt spirals that no real
 * person could sustain — and which made every simulated life identical.
 */
function tickFinance(state: PlayerState, rng: Rng): void {
  const f = state.finance;

  // Interest and amortisation. Payments are capped by ability to pay; unpaid
  // interest capitalises, which is exactly how distress actually works.
  const disposable = disposableIncome(state);
  const serviceBudget = Math.max(0, disposable * 0.42 + Math.max(0, f.cash) * 0.25);
  let serviceSpent = 0;
  for (const d of [...f.debts].sort((a, b) => b.rate - a.rate)) {
    const interest = d.principal * d.rate;
    const wanted = Math.max(d.minPayment, interest);
    const afford = Math.max(0, serviceBudget - serviceSpent);
    const payment = Math.min(wanted, afford);
    serviceSpent += payment;
    d.principal = Math.max(0, d.principal + interest - payment);
  }
  f.debts = f.debts.filter((d) => d.principal > 1);

  // Charge-off ceiling.
  //
  // Unpaid interest capitalises, which is realistic — but only up to a point.
  // Real creditors write off debt they will never recover; without this cap a
  // person with no income accumulates a seven-figure credit-card balance over
  // fifty years, which is arithmetic rather than simulation.
  const unsecuredCap = Math.max(65000, disposable * 6);
  let unsecured = unsecuredDebt(state);
  if (unsecured > unsecuredCap) {
    let excess = unsecured - unsecuredCap;
    for (const d of [...f.debts].sort((a, b) => b.rate - a.rate)) {
      if (excess <= 0) break;
      if (d.kind === 'mortgage' || d.kind === 'student') continue;
      const cut = Math.min(d.principal, excess);
      d.principal -= cut;
      excess -= cut;
    }
    f.debts = f.debts.filter((d) => d.principal > 1);
    state.counters['chargeOffs'] = (state.counters['chargeOffs'] ?? 0) + 1;
    state.flags['badCredit'] = true;
    unsecured = unsecuredDebt(state);
    pushLog(state, {
      category: 'finance',
      tone: 'negative',
      title: 'Debt written off',
      detail: 'A creditor gave up. It will follow you for years.',
      weight: 5,
      tags: ['finance', 'crisis'],
    });
  }

  // Lifestyle drifts up toward what the income supports, unless the player has
  // deliberately chosen austerity (a low spending habit resists the drift).
  const target = lifestyleTargetForIncome(disposable);
  const gap = target - f.lifestyleLevel;
  if (gap > 0) {
    // Deliberate frugality is the only thing that holds spending below income.
    const resistance = (state.habits.saving * 0.6 + (100 - state.habits.spending) * 0.4) / 100;
    const chance = clamp(0.85 - resistance * 0.7, 0.12, 0.9);
    if (rng.keyed(`creep:${state.age}`).chance(chance)) {
      f.lifestyleLevel = clamp(f.lifestyleLevel + (gap >= 3 ? 2 : 1), 0, 10);
    }
  }

  // Involuntary downshift: cut discretionary spending until the year is
  // survivable, up to three tiers.
  const shortfall = (): number => annualExpenses(state) + serviceSpent - disposable - Math.max(0, f.cash);
  let cuts = 0;
  while (shortfall() > 0 && f.lifestyleLevel > 0 && cuts < 3) {
    f.lifestyleLevel -= 1;
    cuts += 1;
  }
  if (cuts > 0) {
    state.stats.happiness = clamp100(state.stats.happiness - cuts * 3);
    state.habits.spending = clamp100(state.habits.spending - cuts * 4);
    if (cuts >= 2) {
      pushLog(state, {
        category: 'finance',
        tone: 'negative',
        title: 'Had to cut back',
        detail: 'The money did not reach the end of the year.',
        weight: 3,
        tags: ['finance', 'hardship'],
      });
    }
  }

  const expenses = annualExpenses(state);
  f.lifetimeEarnings += disposable;
  f.lifetimeSpending += expenses;
  f.cash += disposable - expenses - serviceSpent;

  // Retirement contributions while working.
  if (state.career.status === 'employed' || state.career.status === 'selfEmployed') {
    const rate = (state.flags['pensionMaxed'] ? 0.09 : 0.035) + (state.habits.investing / 100) * 0.04;
    const boost = 1 + (state.counters['retirementBoost'] ?? 0) * 0.3;
    const contribution = Math.min(Math.max(0, f.cash), Math.round(f.salary * rate * boost));
    if (contribution > 0) {
      f.cash -= contribution;
      f.retirementAssets += contribution;
    }
  }

  // Habit-driven automatic investing of genuine surplus.
  const monthly = Math.max(1, expenses / 12);
  const buffer = monthly * f.emergencyMonthsTarget;
  const surplus = f.cash - buffer;
  if (surplus > 1500 && state.habits.investing > 18) {
    const fraction = clamp((state.habits.investing - 10) / 200, 0.05, 0.45);
    const amount = Math.round(surplus * fraction);
    const pick = state.traits.riskTolerance > 62 ? 'growth' : state.traits.riskTolerance < 38 ? 'conservative' : 'balanced';
    f.cash -= amount;
    f.holdings[pick].value += amount;
    f.holdings[pick].contributed += amount;
  }

  // Money left sitting far beyond any conceivable buffer gets put somewhere,
  // even by people with no interest in investing.
  const idleCeiling = monthly * 24;
  if (f.cash > idleCeiling) {
    const move = Math.round((f.cash - idleCeiling) * 0.5);
    f.cash -= move;
    f.holdings['conservative'].value += move;
    f.holdings['conservative'].contributed += move;
  }

  // Cover any remaining shortfall by borrowing — but only within capacity.
  if (f.cash < 0) {
    const need = -f.cash;
    f.cash = 0;
    const capacity = Math.max(9000, disposable * 3.5) - unsecuredDebt(state);
    const borrow = Math.min(need, Math.max(0, capacity));
    if (borrow > 200) {
      applyEffects({ state, rng }, [
        { k: 'debt', op: 'add', kind: 'consumer', label: 'Overdraft', amount: borrow, rate: 0.196, years: 5 },
      ]);
    }
    const unmet = need - borrow;
    if (unmet > 200) {
      // Beyond borrowing capacity the household simply goes without.
      state.flags['hardship'] = true;
      state.stats.stress = clamp100(state.stats.stress + 9);
      state.stats.health = clamp100(state.stats.health - 2.5);
      state.stats.happiness = clamp100(state.stats.happiness - 5);
      state.counters['hardshipYears'] = (state.counters['hardshipYears'] ?? 0) + 1;
      f.lifestyleLevel = 0;
    } else {
      state.flags['hardship'] = false;
    }
  } else {
    state.flags['hardship'] = false;
  }

  const nw = netWorth(state);
  if (nw > f.peakNetWorth) f.peakNetWorth = nw;
}

/* ------------------------------------------------------------------ *
 * Health, energy, stress, burnout
 * ------------------------------------------------------------------ */

function ageDecline(age: number): number {
  if (age < 30) return 0.05;
  if (age < 45) return 0.32;
  if (age < 60) return 0.78;
  if (age < 72) return 1.5;
  if (age < 82) return 2.3;
  return 3.1;
}

function tickHealth(state: PlayerState, rng: Rng): void {
  const s = state.stats;
  let delta = -ageDecline(state.age);
  delta += ((state.habits.exercise - 45) / 100) * 2.1;
  delta += ((state.habits.sleep - 45) / 100) * 1.3;
  if (s.stress > 55) delta -= ((s.stress - 55) / 45) * 1.9;
  if (state.burnout.stage === 'burntOut') delta -= 2.2;
  for (const c of state.conditions) delta -= c.severity / 150;
  // Money buys health at the margin: better food, earlier care, less precarity.
  delta += (financialSecurity(state) / 100 - 0.5) * 0.7;
  delta += rng.keyed(`health:${state.age}`).normal(0, 0.7);
  s.health = clamp100(s.health + delta);

  // Chronic conditions worsen slowly if health is poor.
  for (const c of state.conditions) {
    if (c.chronic && s.health < 55) c.severity = clamp100(c.severity + 1.5);
    else if (!c.chronic && s.health > 65) c.severity = Math.max(0, c.severity - 4);
  }
  state.conditions = state.conditions.filter((c) => c.severity > 1);

  // Frailty is the hidden mortality modifier: accumulated damage.
  const frailtyTarget = clamp100(
    (100 - s.health) * 0.6 + state.conditions.reduce((a, c) => a + c.severity * 0.25, 0),
  );
  state.frailty = clamp100(state.frailty + (frailtyTarget - state.frailty) * 0.35);
}

function tickEnergyAndStress(state: PlayerState): void {
  const s = state.stats;
  const load = lifeLoad(state);
  const support = supportNetwork(state);

  const energyTarget = clamp100(
    28 +
      (s.health / 100) * 34 +
      (state.habits.sleep / 100) * 22 +
      (state.habits.exercise / 100) * 14 -
      (load / 100) * 26 -
      (s.stress / 100) * 24 -
      (state.burnout.load / 100) * 18,
  );
  s.energy = clamp100(s.energy + (energyTarget - s.energy) * 0.45);

  const stressTarget = clamp100(
    18 +
      (load / 100) * 46 +
      (debtPressure(state) / 100) * 26 +
      Math.max(0, 12 - runwayMonths(state)) * 1.7 +
      (100 - state.family.householdHarmony) * 0.12 +
      state.conditions.reduce((a, c) => a + c.severity * 0.14, 0) +
      (state.inertia.pressure / 100) * 14 +
      (state.flags['hardship'] ? 10 : 0) -
      (support / 100) * 14 -
      (state.habits.exercise / 100) * 9 -
      (state.habits.sleep / 100) * 7 -
      (s.purpose / 100) * 6,
  );
  s.stress = clamp100(s.stress + (stressTarget - s.stress) * 0.48);
}

const BURNOUT_ORDER: readonly BurnoutStage[] = [
  'healthy',
  'overextended',
  'exhausted',
  'atRisk',
  'burntOut',
  'recovering',
  'rebuilding',
];

function tickBurnout(state: PlayerState): void {
  const b = state.burnout;
  const s = state.stats;
  const load = lifeLoad(state);

  // Burnout is driven primarily by sustained *load*, not by momentary stress.
  // Tying it to stress alone meant a well-supported, well-paid workaholic never
  // burnt out, which is the opposite of how this actually goes.
  let delta = 0;
  const workload = state.career.workload;
  const working =
    state.career.status === 'employed' ||
    state.career.status === 'selfEmployed' ||
    state.career.status === 'founder';
  if (working && workload > 62) delta += (workload - 62) / 4;
  if (load > 58) delta += (load - 58) / 6;
  if (s.stress > 55) delta += (s.stress - 55) / 6;
  if (s.purpose < 35) delta += (35 - s.purpose) / 12;
  if (state.habits.workaholism > 60) delta += (state.habits.workaholism - 60) / 12;
  if (state.habits.sleep < 40) delta += (40 - state.habits.sleep) / 14;
  // Recovery requires actual slack, not merely the absence of catastrophe.
  if ((!working || workload < 56) && s.stress < 50) delta -= 6 + (s.energy / 100) * 5;
  if (b.stage === 'recovering') delta -= 9;
  if (b.stage === 'rebuilding') delta -= 4;
  delta -= (supportNetwork(state) / 100) * 2;

  b.load = clamp100(b.load + delta);
  b.yearsInStage += 1;

  const previous = b.stage;
  if (b.stage === 'burntOut') {
    // Only an explicit recovery choice leaves this stage; see the event chain.
    if (b.load < 45) b.stage = 'recovering';
  } else if (b.stage === 'recovering') {
    if (b.load < 28) b.stage = 'rebuilding';
  } else if (b.stage === 'rebuilding') {
    if (b.load < 20) b.stage = 'healthy';
    else if (b.load > 70) b.stage = 'atRisk';
  } else {
    if (b.load >= 88) b.stage = 'burntOut';
    else if (b.load >= 72) b.stage = 'atRisk';
    else if (b.load >= 54) b.stage = 'exhausted';
    else if (b.load >= 36) b.stage = 'overextended';
    else b.stage = 'healthy';
  }

  if (b.stage !== previous) {
    b.yearsInStage = 0;
    const worsened = BURNOUT_ORDER.indexOf(b.stage) > BURNOUT_ORDER.indexOf(previous) && b.stage !== 'recovering' && b.stage !== 'rebuilding';
    if (b.stage === 'burntOut') {
      b.episodes += 1;
      b.lastEpisodeAge = state.age;
      if (!state.pendingEvents.includes('wellbeing.burnoutHit')) {
        state.pendingEvents.push('wellbeing.burnoutHit');
      }
    } else if (worsened || b.stage === 'recovering') {
      pushLog(state, {
        category: 'wellbeing',
        tone: worsened ? 'negative' : 'positive',
        title:
          b.stage === 'atRisk'
            ? 'Running on empty'
            : b.stage === 'exhausted'
              ? 'Genuinely exhausted'
              : b.stage === 'overextended'
                ? 'Overextended'
                : 'Starting to recover',
        weight: 3,
        tags: ['burnout', b.stage],
      });
    }
  }
}

/* ------------------------------------------------------------------ *
 * Happiness and purpose
 * ------------------------------------------------------------------ */

function tickWellbeing(state: PlayerState): void {
  const s = state.stats;
  const social = socialHealth(state);
  const security = financialSecurity(state);
  const regret = activeRegretWeight(state);

  const happinessTarget = clamp100(
    8 +
      (social / 100) * 26 +
      (security / 100) * 16 +
      (s.health / 100) * 16 +
      (s.purpose / 100) * 14 +
      lifestyleComfort(state.finance.lifestyleLevel) * 0.6 +
      (state.habits.socialising / 100) * 6 -
      (s.stress / 100) * 22 -
      Math.min(18, regret * 0.22) -
      (state.burnout.load / 100) * 10,
  );
  s.happiness = clamp100(s.happiness + (happinessTarget - s.happiness) * 0.38);

  let aspirationDrive = 0;
  for (const a of state.aspirations) {
    if (a.status === 'active') aspirationDrive += a.progress / 100;
    if (a.status === 'achieved') aspirationDrive += 1.2;
    if (a.status === 'failed') aspirationDrive -= 0.5;
  }
  const contributions = state.counters['contributions'] ?? 0;
  const creative = state.counters['creativeWorks'] ?? 0;

  const purposeTarget = clamp100(
    12 +
      (state.career.status === 'retired' ? 0 : (state.career.meaningAlignment / 100) * 22) +
      Math.min(18, aspirationDrive * 7) +
      Math.min(14, contributions * 3) +
      Math.min(10, creative * 3) +
      (children(state).length > 0 ? 12 : 0) +
      (state.family.maritalStatus === 'married' || state.family.maritalStatus === 'partnered' ? 6 : 0) +
      (state.habits.learning / 100) * 6 -
      Math.min(14, regret * 0.18),
  );
  s.purpose = clamp100(s.purpose + (purposeTarget - s.purpose) * 0.3);
}

/* ------------------------------------------------------------------ *
 * Relationships
 * ------------------------------------------------------------------ */

function tickRelationships(state: PlayerState, rng: Rng): void {
  const care = state.habits.relationshipCare / 100;
  const load = lifeLoad(state);
  const stream = rng.keyed(`rel:${state.age}`);

  for (const p of state.people) {
    if (!p.alive) continue;
    p.age += 1;

    const yearsSince = state.age - p.lastInteractionAge;
    const isHousehold = p.kind === 'spouse' || p.kind === 'partner' || (p.kind === 'child' && p.age < 19);
    const decayRate = isHousehold ? 1.1 : p.kind === 'parent' || p.kind === 'sibling' ? 2.1 : 3.2;
    const decay = Math.max(0, (yearsSince - (isHousehold ? 0 : 1))) * decayRate * (1 - care * 0.55);
    const maintenance = care * (isHousehold ? 6 : 3.4) - (load / 100) * 2.6;

    p.bond.closeness = clamp100(p.bond.closeness - decay + maintenance);
    p.bond.trust = clamp100(p.bond.trust - decay * 0.35 + maintenance * 0.4);
    p.bond.support = clamp100(
      p.bond.support + (p.bond.trust * 0.5 + p.bond.closeness * 0.5 - p.bond.support) * 0.2,
    );
    // Conflict eases with compatibility and time, worsens under strain.
    const conflictDrift =
      (load > 70 ? 2.2 : -1.4) + (p.bond.compatibility < 40 ? 1.6 : -0.9) + (state.stats.stress > 70 ? 1.4 : 0);
    p.bond.conflict = clamp100(p.bond.conflict + conflictDrift * (isHousehold ? 1.3 : 0.6));

    if (isHousehold) p.lastInteractionAge = state.age;

    // Ageing relatives eventually need care, then die.
    if (p.kind === 'parent' && p.age > 72 && !p.meta['needsCare'] && stream.chance(0.14)) {
      p.meta['needsCare'] = true;
    }
    if (p.age > 74) {
      const hazard = clamp(0.012 * Math.exp(0.1 * (p.age - 74)), 0, 0.5);
      if (stream.chance(hazard)) {
        p.alive = false;
        p.tags.push('died');
        if (p.kind === 'spouse') {
          state.family.maritalStatus = 'widowed';
          state.stats.happiness = clamp100(state.stats.happiness - 24);
          pushLog(state, {
            category: 'relationship',
            tone: 'negative',
            title: `${p.name} died`,
            weight: 10,
            tags: ['loss', 'spouse'],
          });
        } else if (p.kind === 'parent' || p.kind === 'sibling' || p.kind === 'friend') {
          state.stats.happiness = clamp100(state.stats.happiness - 8);
          pushLog(state, {
            category: 'relationship',
            tone: 'negative',
            title: `${p.name} died`,
            weight: 6,
            tags: ['loss', p.kind],
          });
        }
      }
    }

    // Children leave home.
    if (p.kind === 'child' && p.age === 19) {
      state.family.dependents = Math.max(0, state.family.dependents - 1);
      pushLog(state, {
        category: 'family',
        tone: 'neutral',
        title: `${p.name} moved out`,
        weight: 5,
        tags: ['family', 'milestone'],
      });
    }
  }

  // Household harmony is a function of the primary bond plus pressure.
  const primary = state.people.find((p) => p.alive && (p.kind === 'spouse' || p.kind === 'partner'));
  if (primary) {
    const target = clamp100(
      primary.bond.closeness * 0.45 + primary.bond.trust * 0.3 + primary.bond.compatibility * 0.2 - primary.bond.conflict * 0.5 - (load / 100) * 12,
    );
    state.family.householdHarmony = clamp100(
      state.family.householdHarmony + (target - state.family.householdHarmony) * 0.4,
    );
  } else {
    state.family.householdHarmony = clamp100(state.family.householdHarmony + (58 - state.family.householdHarmony) * 0.2);
  }
}

/* ------------------------------------------------------------------ *
 * Inertia
 * ------------------------------------------------------------------ */

/**
 * The probability that a stated intention quietly fails to happen. This is the
 * procrastination system's core: it is never a flat dice roll, it is a function
 * of how depleted and how avoidant the player currently is.
 */
export function inertiaChance(state: PlayerState, baseRisk: number): number {
  if (baseRisk <= 0) return 0;
  const depletion = (100 - state.stats.energy) / 100;
  const pressure = state.inertia.pressure / 100;
  const avoidance = state.habits.procrastination / 100;
  const discipline = state.traits.discipline / 100;
  const stress = Math.max(0, state.stats.stress - 55) / 45;
  const load = Math.max(0, lifeLoad(state) - 60) / 40;
  const modifier =
    0.45 + depletion * 0.5 + pressure * 0.45 + avoidance * 0.55 + stress * 0.35 + load * 0.3 - discipline * 0.75;
  return clamp(baseRisk * clamp(modifier, 0.15, 2.4), 0, 0.85);
}

function tickInertia(state: PlayerState): Effect[] {
  const queued: Effect[] = [];
  const surviving = [];
  for (const i of state.inertia.intentions) {
    const yearsOpen = state.age - i.createdAge;
    i.urgency = clamp100(i.urgency + 1.5 + yearsOpen * 0.3);
    // The window closes sooner for urgent things.
    const lifespan = clamp(Math.round(22 - i.urgency / 6), 4, 20);
    if (yearsOpen >= lifespan) {
      state.inertia.decayedCount += 1;
      state.inertia.pressure = clamp100(state.inertia.pressure + 6);
      if (i.decayEventId) queued.push({ k: 'triggerEvent', eventId: i.decayEventId });
      pushLog(state, {
        category: 'inertia',
        tone: 'negative',
        title: `Never got round to it: ${i.label}`,
        weight: 4,
        tags: ['inertia', i.topic],
      });
      continue;
    }
    surviving.push(i);
  }
  state.inertia.intentions = surviving;

  const openPressure = state.inertia.intentions.reduce((a, i) => a + i.urgency, 0) / 12;
  const target = clamp100(
    10 + openPressure + state.habits.procrastination * 0.35 + Math.max(0, state.stats.stress - 55) * 0.3 - state.traits.discipline * 0.2,
  );
  state.inertia.pressure = clamp100(state.inertia.pressure + (target - state.inertia.pressure) * 0.3);
  return queued;
}

/* ------------------------------------------------------------------ *
 * Habits drift
 * ------------------------------------------------------------------ */

function tickHabits(state: PlayerState): void {
  const h = state.habits;
  const load = lifeLoad(state);
  const drift = (key: keyof typeof h, target: number, rate = 0.16) => {
    h[key] = clamp100(h[key] + (target - h[key]) * rate);
  };
  // Habits regress toward what your circumstances make easy.
  drift('exercise', clamp100(52 - (load - 55) * 0.5 + (state.traits.discipline - 50) * 0.3));
  drift('sleep', clamp100(58 - (load - 55) * 0.55 - Math.max(0, state.stats.stress - 55) * 0.3));
  drift('workaholism', clamp100(state.career.workload * 0.7 + (state.traits.ambition - 50) * 0.35));
  drift('saving', clamp100(38 + (financialSecurity(state) - 50) * 0.2 + (state.traits.patience - 50) * 0.3));
  drift('spending', clamp100(40 + (state.finance.lifestyleLevel - 3) * 4 + (state.traits.impulsiveness - 50) * 0.3));
  drift('procrastination', clamp100(42 + state.inertia.pressure * 0.3 - state.traits.discipline * 0.3 + Math.max(0, state.stats.stress - 55) * 0.25));
  drift('socialising', clamp100(44 - (load - 55) * 0.35 + (state.traits.socialConfidence - 50) * 0.35));
  drift('relationshipCare', clamp100(44 - (load - 55) * 0.4 + (state.traits.empathy - 50) * 0.3));
  drift('learning', clamp100(36 + (state.traits.curiosity - 50) * 0.4 - (load - 55) * 0.2), 0.12);
  drift('investing', clamp100(30 + (state.skills.financial - 30) * 0.4 + (state.traits.patience - 50) * 0.25), 0.12);
}

/* ------------------------------------------------------------------ *
 * Aspirations
 * ------------------------------------------------------------------ */

function tickAspirations(state: PlayerState): Effect[] {
  const queued: Effect[] = [];
  for (const a of state.aspirations) {
    if (a.status !== 'active') continue;
    const def = ASPIRATIONS[a.defId];
    if (!def) continue;

    if (def.progress.k === 'metric') {
      const value = queryNumber(state, def.progress.path) ?? 0;
      a.progress = clamp100((value / def.progress.target) * 100);
    } else if (def.progress.k === 'flag') {
      if (state.flags[def.progress.flag]) a.progress = 100;
    }

    if (a.progress >= 100) {
      a.status = 'achieved';
      a.resolvedAge = state.age;
      queued.push(...def.onAchieve);
      pushLog(state, {
        category: def.category,
        tone: 'milestone',
        title: def.kind === 'dream' ? `Realised a dream: ${def.label}` : `Achieved: ${def.label}`,
        weight: def.kind === 'dream' ? 9 : 6,
        tags: ['aspiration', def.kind],
      });
      continue;
    }

    if (def.regretAge && state.age >= def.regretAge) {
      a.status = 'failed';
      a.resolvedAge = state.age;
      if (def.onMiss) queued.push(...def.onMiss);
      pushLog(state, {
        category: def.category,
        tone: 'negative',
        title: `Let go of: ${def.label}`,
        weight: 6,
        tags: ['aspiration', 'missed'],
      });
    }
  }

  // Manual-progress aspirations resolved by state rather than by events.
  const debtFree = state.aspirations.find((a) => a.defId === 'goal.debtFree' && a.status === 'active');
  if (debtFree && totalDebt(state) <= 0 && state.age > state.startAge + 1) {
    debtFree.progress = 100;
  }
  const mastery = state.aspirations.find((a) => a.defId === 'goal.mastery' && a.status === 'active');
  if (mastery) {
    const best = Math.max(...Object.values(state.skills));
    mastery.progress = Math.max(mastery.progress, clamp100((best / 85) * 100));
  }
  const partnership = state.aspirations.find((a) => a.defId === 'dream.partnership' && a.status === 'active');
  if (partnership && state.family.unionSinceAge !== null) {
    const years = state.age - state.family.unionSinceAge;
    partnership.progress = Math.max(partnership.progress, clamp100((years / 25) * 100));
  }
  const earlyRetire = state.aspirations.find((a) => a.defId === 'dream.earlyRetire' && a.status === 'active');
  if (earlyRetire && state.career.status === 'retired' && state.age < 60) {
    earlyRetire.progress = 100;
  }

  // Regrets soften over time but never quite vanish unresolved.
  for (const r of state.regrets) {
    if (!r.resolved) r.weight = Math.max(4, r.weight - 0.4);
  }
  return queued;
}

/* ------------------------------------------------------------------ *
 * Scheduled effects
 * ------------------------------------------------------------------ */

function tickScheduled(ctx: EffectContext): void {
  const { state } = ctx;
  const due = state.scheduled.filter((s) => s.dueAge <= state.age);
  if (due.length === 0) return;
  state.scheduled = state.scheduled.filter((s) => s.dueAge > state.age);

  for (const item of due) {
    if (item.requires && !evaluateAll(state, item.requires)) {
      pushLog(state, {
        category: 'system',
        tone: 'neutral',
        title: `Avoided: ${item.sourceLabel}`,
        detail: 'A consequence set in motion years ago no longer applied.',
        weight: 1,
        tags: ['delayed', 'avoided'],
      });
      continue;
    }
    if (item.effects.length > 0) {
      applyEffects({ ...ctx, source: item.sourceLabel, sourceEventId: item.sourceEventId }, item.effects);
      pushLog(state, {
        category: 'system',
        tone: 'neutral',
        title: item.sourceLabel,
        detail: item.narrative ?? `Set in motion at ${item.sourceAge}.`,
        weight: 4,
        tags: ['delayed', `from:${item.sourceAge}`],
      });
    }
    if (item.eventId && !state.pendingEvents.includes(item.eventId)) {
      state.pendingEvents.push(item.eventId);
    }
  }
}

/* ------------------------------------------------------------------ *
 * Crisis checks and mortality
 * ------------------------------------------------------------------ */

function queue(state: PlayerState, eventId: string): void {
  if (!state.pendingEvents.includes(eventId)) state.pendingEvents.push(eventId);
}

function checkCrises(state: PlayerState): void {
  const debt = unsecuredDebt(state);
  const disposable = disposableIncome(state);
  const bankruptcies = state.counters['bankruptcies'] ?? 0;
  const lastSeen = state.eventMemory.lastSeenAge['crisis.bankruptcy'];
  const recently = lastSeen !== undefined && state.age - lastSeen < 12;

  // Insolvency requires all of: a real debt load, no liquidity, negative net
  // worth, and no path to servicing it. Students and the very young are
  // excluded — a student loan is not a bankruptcy.
  const insolvent =
    state.age >= 24 &&
    state.career.status !== 'student' &&
    debt > Math.max(32000, disposable * 2.5) &&
    state.finance.cash < Math.max(2000, disposable * 0.1) &&
    netWorth(state) < -Math.max(20000, disposable) &&
    (state.counters['hardshipYears'] ?? 0) >= 2;

  if (insolvent && !recently && bankruptcies < 2) {
    queue(state, 'crisis.bankruptcy');
  }

  // Systemic backstop.
  //
  // The bankruptcy *event* gives a player the choice of how to handle
  // insolvency. But the core must stay bounded even when nothing is drawing
  // events — the balance lab drives `advanceYears` directly, and a headless run
  // must not be able to spiral forever.
  if ((state.counters['hardshipYears'] ?? 0) >= 8) {
    state.counters['hardshipYears'] = 0;
    for (const d of state.finance.debts) {
      if (d.kind === 'mortgage' || d.kind === 'student') continue;
      d.principal = Math.round(d.principal * 0.1);
    }
    state.finance.debts = state.finance.debts.filter((d) => d.principal > 1);
    state.counters['bankruptcies'] = bankruptcies + 1;
    state.flags['wasBankrupt'] = true;
    state.flags['badCredit'] = true;
    state.finance.lifestyleLevel = 0;
    state.stats.stress = clamp100(state.stats.stress + 12);
    state.stats.happiness = clamp100(state.stats.happiness - 14);
    state.traits.resilience = clamp100(state.traits.resilience + 6);
    pushLog(state, {
      category: 'crisis',
      tone: 'negative',
      title: 'Insolvent',
      detail: 'Years of shortfall ended in a formal discharge.',
      weight: 9,
      tags: ['crisis', 'bankruptcy'],
    });
  }
}

/**
 * The life spine.
 *
 * Board-driven event draws are the texture of the game, but a few decisions are
 * structural: everyone must choose what to do after school, everyone must at
 * some point start earning, and nobody stays unemployed for a decade without
 * the game asking about it. These are forced rather than drawn, because leaving
 * them to chance produced lives with no income at all.
 */
function ensureSpine(state: PlayerState): void {
  const c = state.career;

  if (!state.flags['chosePath'] && state.age >= 18 && state.age <= 24) {
    queue(state, 'education.firstChoice');
    return;
  }

  if (c.status === 'none' && state.age >= 20) {
    queue(state, 'career.firstJob');
    return;
  }

  if (c.status === 'unemployed' || c.status === 'break') {
    state.counters['yearsIdle'] = (state.counters['yearsIdle'] ?? 0) + 1;
    if ((state.counters['yearsIdle'] ?? 0) >= 2 && state.age < 66) {
      state.counters['yearsIdle'] = 0;
      queue(state, 'career.unemployed');
    }
  } else {
    state.counters['yearsIdle'] = 0;
  }

  // The relationship arc must be *offered* reliably. Leaving first meetings,
  // proposals and the question of children to random board draws meant most
  // simulated lives never encountered any of them — which is not a life.
  const marital = state.family.maritalStatus;
  const partner = state.people.find((p) => p.alive && (p.kind === 'partner' || p.kind === 'spouse'));

  if (!partner && (marital === 'single' || marital === 'divorced' || marital === 'widowed')) {
    const lastMeeting = state.eventMemory.lastSeenAge['romance.meeting'] ?? -99;
    const gap = state.age - lastMeeting;
    const window = state.age < 45 ? 5 : 8;
    if (state.age >= 20 && state.age <= 72 && gap >= window) {
      queue(state, 'romance.meeting');
    }
  }

  if (partner && (marital === 'dating' || marital === 'partnered')) {
    const together = state.family.unionSinceAge === null ? 0 : state.age - state.family.unionSinceAge;
    const lastAsked = state.eventMemory.lastSeenAge['romance.proposal'] ?? -99;
    const gap = state.age - lastAsked;
    const wantsAsking = marital === 'dating' ? gap >= 4 : together >= 4 && gap >= 10 && state.age <= 55;
    if (wantsAsking && state.age >= 22) queue(state, 'romance.proposal');
  }

  if (
    (marital === 'married' || marital === 'partnered') &&
    state.age >= 25 &&
    state.age <= 44 &&
    state.family.childrenBorn < 3 &&
    !state.flags['choseNoChildren']
  ) {
    const asked = state.eventMemory.lastSeenAge['family.childQuestion'] ?? -99;
    if (state.age - asked >= 4) queue(state, 'family.childQuestion');
  }

  // Someone approaching the end of their working life without ever having
  // addressed retirement gets asked once.
  if (state.age === 64 && c.status !== 'retired' && !state.flags['retirementAsked']) {
    state.flags['retirementAsked'] = true;
    queue(state, 'milestone.sixtyFour');
  }
}

/**
 * Annual probability of dying.
 *
 * A Gompertz baseline modulated by frailty, habits, stress and social
 * connection. One deliberate departure from realism: natural death is
 * suppressed below 28 and ramps in gradually to 38. Real young adults do die,
 * but a life that ends at 21 after four decisions is not a story — it is a lost
 * session. Lethal *events* can still end a young life; chance alone cannot.
 */
function mortalityHazard(state: PlayerState): number {
  if (state.age < 28) return 0;
  const youngRamp = state.age >= 38 ? 1 : (state.age - 28) / 10;
  // Gompertz baseline, modulated by accumulated frailty and behaviour.
  const base = 0.00016 * Math.exp(0.0935 * (state.age - 22)) * youngRamp;
  const frailtyMult = 1 + (state.frailty / 100) * 2.6;
  const habitMult = 1 + (0.5 - state.habits.exercise / 200) * 0.5 - (state.habits.sleep / 100) * 0.12;
  const stressMult = 1 + Math.max(0, state.stats.stress - 65) / 120;
  const socialMult = 1 + (0.6 - socialHealth(state) / 160);
  return clamp(base * frailtyMult * habitMult * stressMult * socialMult, 0, 0.75);
}

function checkMortality(state: PlayerState, rng: Rng): boolean {
  if (state.age >= 104) {
    state.alive = false;
    state.causeOfDeath = 'old age';
    return true;
  }
  const hazard = mortalityHazard(state);
  if (rng.keyed(`mortality:${state.age}`).chance(hazard)) {
    state.alive = false;
    state.causeOfDeath =
      state.conditions.length > 0
        ? `complications from ${state.conditions[0]?.label.toLowerCase()}`
        : state.age > 75
          ? 'old age'
          : 'sudden illness';
    return true;
  }
  return false;
}

/* ------------------------------------------------------------------ *
 * Public entry point
 * ------------------------------------------------------------------ */

export interface YearTickResult {
  died: boolean;
  years: number;
}

/** Advance the simulation by `years`, one year at a time. */
export function advanceYears(state: PlayerState, years: number): YearTickResult {
  let advanced = 0;
  for (let i = 0; i < years; i++) {
    if (!state.alive) break;
    const rng = Rng.fromState(state.rng);
    state.age += 1;
    advanced += 1;
    state.stage = stageForAge(state.age);

    updateMarket(state, rng);
    tickEducation(state);
    tickCareer(state, rng);
    tickBusiness(state, rng);
    applyInvestmentReturns(state, rng);
    tickFinance(state, rng);
    tickHealth(state, rng);
    tickEnergyAndStress(state);
    tickBurnout(state);
    tickRelationships(state, rng);
    tickHabits(state);
    tickWellbeing(state);

    // Running averages. "Was this a happy life?" is not the same question as
    // "were you happy on the last day of it", and the ending should ask the
    // former.
    state.counters['yearsLived'] = (state.counters['yearsLived'] ?? 0) + 1;
    state.counters['happinessSum'] = (state.counters['happinessSum'] ?? 0) + state.stats.happiness;
    state.counters['purposeSum'] = (state.counters['purposeSum'] ?? 0) + state.stats.purpose;
    state.counters['healthSum'] = (state.counters['healthSum'] ?? 0) + state.stats.health;
    state.counters['stressSum'] = (state.counters['stressSum'] ?? 0) + state.stats.stress;

    const ctx: EffectContext = { state, rng, source: 'yearly' };
    const inertiaQueued = tickInertia(state);
    const aspirationQueued = tickAspirations(state);
    applyEffects(ctx, [...inertiaQueued, ...aspirationQueued]);
    tickScheduled(ctx);
    checkCrises(state);
    ensureSpine(state);

    state.rng = rng.toState();

    if (checkMortality(state, rng)) {
      state.rng = rng.toState();
      return { died: true, years: advanced };
    }
    state.rng = rng.toState();
  }
  state.updatedAt = Date.now();
  return { died: !state.alive, years: advanced };
}
